import { Link } from 'react-router-dom';
import { formatDate } from '@/modules/marketing/blog/posts';
import type { BlogPost } from '@/modules/marketing/blog/posts';

/**
 * A post as a card, on the index and under every post.
 *
 * ## One link per card
 *
 * The title is the link and its `::after` is stretched over the whole card, so the card is one
 * target for a mouse and one stop for a screen reader. An image wrapped in a second link to the
 * same address reads as the same link twice.
 *
 * ## The heading level follows where the card is
 *
 * On the index the cards are the page's sections, so each title is an h2. Under a post they sit
 * beneath a "Keep reading" h2, so they are h3 there, and the outline stays a tree.
 *
 * ## The picture has no alt text here
 *
 * Beside a heading that names the post it is decoration, and a screen reader reading the same
 * sentence twice is not an improvement. The post's own page carries the descriptive alt text.
 */

const STRETCHED = "after:absolute after:inset-0 after:content-['']";

export function BlogCard({ post, level = 2 }: { post: BlogPost; level?: 2 | 3 }) {
  const Heading = level === 2 ? 'h2' : 'h3';

  return (
    <article className="border-border/60 hover:border-primary/40 group bg-card/40 relative flex flex-col overflow-hidden rounded-xl border transition-colors">
      <img
        src={post.image.src}
        alt=""
        width={post.image.width}
        height={post.image.height}
        loading="lazy"
        decoding="async"
        className="border-border/60 aspect-video w-full border-b object-cover"
      />
      <div className="flex flex-1 flex-col p-5">
        <p className="text-primary text-xs font-medium tracking-[0.16em] uppercase">{post.topic}</p>
        <Heading className="mt-2.5 text-lg leading-snug font-semibold tracking-tight text-balance">
          <Link to={`/blog/${post.slug}`} className={`group-hover:text-primary transition-colors ${STRETCHED}`}>
            {post.title}
          </Link>
        </Heading>
        <p className="text-muted-foreground mt-3 flex-1 text-sm leading-relaxed">{post.description}</p>
        <p className="text-muted-foreground/80 mt-4 text-xs">
          <time dateTime={post.published}>{formatDate(post.published)}</time>
          <span aria-hidden> · </span>
          {post.minutes} min read
        </p>
      </div>
    </article>
  );
}

/** The newest post, set larger above the grid. */
export function FeaturedBlogCard({ post }: { post: BlogPost }) {
  return (
    <article className="border-border/60 hover:border-primary/40 group bg-card/40 relative grid overflow-hidden rounded-2xl border transition-colors lg:grid-cols-[1.25fr_1fr]">
      <img
        src={post.image.src}
        alt=""
        width={post.image.width}
        height={post.image.height}
        decoding="async"
        className="border-border/60 aspect-video w-full border-b object-cover lg:aspect-auto lg:h-full lg:border-r lg:border-b-0"
      />
      <div className="flex flex-col justify-center p-7 sm:p-9">
        <p className="text-primary text-xs font-medium tracking-[0.16em] uppercase">{post.topic}</p>
        <h2 className="mt-3 text-2xl leading-tight font-semibold tracking-tight text-balance sm:text-3xl">
          <Link to={`/blog/${post.slug}`} className={`group-hover:text-primary transition-colors ${STRETCHED}`}>
            {post.title}
          </Link>
        </h2>
        <p className="text-muted-foreground mt-4 leading-relaxed">{post.description}</p>
        <p className="text-muted-foreground/80 mt-5 text-sm">
          <time dateTime={post.published}>{formatDate(post.published)}</time>
          <span aria-hidden> · </span>
          {post.minutes} min read
        </p>
      </div>
    </article>
  );
}
